import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useApiClientsOrNull } from '../context/ApiClientsContext';
import type { RootState, AppDispatch } from '@center-pwa/store/store';
import type { UseCaseRow } from 'types/schema';
import { fetchUseCases } from '@center-pwa/reducers/schemasReducer';

export interface UseUseCasesReturn {
  useCases: UseCaseRow[];
  loading: boolean;
  error: string | null;
}

/**
 * Lazy hook for use-cases. Dispatches fetchUseCases on first mount when
 * useCasesStatus is idle — never at app launch. Subsequent mounts are
 * no-ops (succeeded / loading / failed states block re-dispatch).
 */
export function useUseCases(): UseUseCasesReturn {
  const dispatch = useDispatch<AppDispatch>();
  const apiClients = useApiClientsOrNull();
  const locale = useSelector((state: RootState) => state.userProfile.locale ?? undefined);

  const useCases = useSelector((state: RootState) => state.schemas.useCases);
  const useCasesStatus = useSelector((state: RootState) => state.schemas.useCasesStatus);
  const error = useSelector((state: RootState) => state.schemas.useCasesError);

  useEffect(() => {
    if (useCasesStatus !== 'idle' || !apiClients?.schemaClient) return;
    dispatch(
      fetchUseCases({
        locale,
        apiClient: apiClients.schemaClient,
      }),
    );
  }, [useCasesStatus, apiClients, dispatch, locale]);

  return {
    useCases,
    loading: useCasesStatus === 'loading',
    error,
  };
}
